<template>
  <StatsWidget
    :title="$t('stats_dashboard.kpis.title')"
    icon="bullseye"
    tone="brand"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="!!widget.data"
    :span-full="true"
    @refresh="$emit('refresh')"
  >
    <div class="stats-kpis">
      <article
        v-for="kpi in kpis"
        :key="kpi.key"
        class="stats-kpis__card"
        :class="`stats-kpis__card--${kpi.tone}`"
      >
        <div class="stats-kpis__card-header">
          <span class="stats-kpis__label-group">
            <span class="stats-kpis__label">{{ kpi.label }}</span>
            <span
              v-if="kpi.tooltip"
              class="stats-kpis__info"
              tabindex="0"
              :aria-label="kpi.label"
              v-tippy="{ content: kpi.tooltip }"
            >
              <BaseIcon name="information-circle" size="13" />
            </span>
          </span>
          <span v-if="kpi.icon" class="stats-kpis__icon" :class="`stats-kpis__icon--${kpi.tone}`">
            <BaseIcon :name="kpi.icon" size="14" />
          </span>
        </div>
        <div class="stats-kpis__value">{{ kpi.value }}</div>
        <div class="stats-kpis__sub">{{ kpi.sub }}</div>
      </article>
    </div>
  </StatsWidget>
</template>

<script>
  /**
   * Renders the 5 headline KPIs from §4 of the backend stats guide.
   * The card layout is intentionally close to ServiceTitan / Jobber
   * dashboards: large primary metric, small contextual count below.
   */
  import { computed, getCurrentInstance } from 'vue';
  import StatsWidget from './StatsWidget.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { formatCurrency, formatCurrencyPrecise, formatPercent } from '@/utils/statsFormat';

  export default {
    name: 'StatsKpis',

    components: { StatsWidget, BaseIcon },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const data = computed(() => props.widget.data || {});
      const counts = computed(() => data.value.counts || {});

      const kpis = computed(() => [
        {
          key: 'sold',
          label: $t('stats_dashboard.kpis.sold'),
          tooltip: $t('stats_dashboard.kpis.sold_tooltip'),
          value: formatCurrency(data.value.sold),
          sub: $t('stats_dashboard.kpis.counts.accepted_proposals', {
            n: counts.value.accepted_proposals ?? 0,
          }),
          icon: 'paper-plane',
          tone: 'brand',
        },
        {
          key: 'planned',
          label: $t('stats_dashboard.kpis.planned'),
          tooltip: $t('stats_dashboard.kpis.planned_tooltip'),
          value: formatCurrency(data.value.planned),
          sub: $t('stats_dashboard.kpis.counts.planned_work_orders', {
            n: counts.value.planned_work_orders ?? 0,
          }),
          icon: 'calendar',
          tone: 'indigo',
        },
        {
          key: 'installed',
          label: $t('stats_dashboard.kpis.installed'),
          tooltip: $t('stats_dashboard.kpis.installed_tooltip'),
          value: formatCurrency(data.value.installed),
          sub: $t('stats_dashboard.kpis.counts.completed_work_orders', {
            n: counts.value.completed_work_orders ?? 0,
          }),
          icon: 'tool',
          tone: 'sky',
        },
        {
          key: 'collected',
          label: $t('stats_dashboard.kpis.collected'),
          value: formatCurrency(data.value.collected),
          sub: $t('stats_dashboard.kpis.counts.paid_invoices', {
            n: counts.value.paid_invoices ?? 0,
          }),
          icon: 'check-circle',
          tone: 'green',
        },
        {
          key: 'avg_ticket',
          label: $t('stats_dashboard.kpis.avg_ticket'),
          value: formatCurrencyPrecise(data.value.avg_ticket),
          sub: $t('stats_dashboard.kpis.avg_ticket_hint'),
          icon: 'package',
          tone: 'amber',
        },
        {
          key: 'win_rate',
          label: $t('stats_dashboard.kpis.win_rate'),
          value: formatPercent(data.value.win_rate),
          sub: [
            $t('stats_dashboard.kpis.counts.won_deals', { n: counts.value.won_deals ?? 0 }),
            $t('stats_dashboard.kpis.counts.lost_deals', { n: counts.value.lost_deals ?? 0 }),
          ].join(' · '),
          icon: 'star',
          tone: 'violet',
        },
      ]);

      return { kpis };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-kpis {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 0.75rem;

    @media (max-width: 1280px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @include lg-down {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @include sm-down {
      grid-template-columns: 1fr;
    }

    &__card {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      padding: 0.875rem 1rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-100);
      border-radius: 0.625rem;
      min-width: 0;
      transition:
        border-color 0.15s ease,
        transform 0.15s ease;

      @include hover {
        border-color: var(--slate-200);
        transform: translateY(-1px);
      }
    }

    &__card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
    }

    &__label-group {
      display: inline-flex;
      align-items: center;
      gap: 0.3125rem;
      min-width: 0;
    }

    &__label {
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      @include ellipsis(1);
    }

    &__info {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: var(--slate-400);
      cursor: help;
      transition: color 0.15s ease;

      @include hover {
        color: var(--slate-600);
      }

      &:focus-visible {
        outline: 2px solid var(--brand-400);
        outline-offset: 2px;
        border-radius: 0.25rem;
      }
    }

    &__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 0.375rem;
      flex-shrink: 0;

      &--brand {
        background: var(--brand-100);
        color: var(--brand-600);
      }
      &--green {
        background: var(--green-100, #dcfce7);
        color: var(--green-600);
      }
      &--amber {
        background: var(--amber-100, #fef3c7);
        color: var(--amber-600);
      }
      &--sky {
        background: var(--sky-100, #e0f2fe);
        color: var(--sky-600, #0284c7);
      }
      &--indigo {
        background: var(--indigo-100, #e0e7ff);
        color: var(--indigo-600, #4f46e5);
      }
      &--violet {
        background: var(--violet-100, #ede9fe);
        color: var(--violet-600, #7c3aed);
      }
    }

    &__value {
      font-size: 1.5rem;
      font-weight: 700;
      line-height: 1.1;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
      @include ellipsis(1);
    }

    &__sub {
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.3;
      @include ellipsis(1);
    }
  }
</style>
